/**
 * useRealtimeDesktopTarget
 *
 * Resolves the correct Preston realtime avatar VM endpoint for the currently
 * signed-in user and returns a fully composed iframe URL pointing at that VM's
 * `/public/preston-realtime.html`.
 *
 * Tenant routing rules:
 *  - Internal Prest0 users (Justin / Miles / Prest0n) → internal full Preston VM
 *    (the same one Slack uses).
 *  - Everyone else → `accounts/{accountId}/vm/main.baseUrl`, where `accountId`
 *    is the canonical account resolved via `resolveAccount(authUid)`.
 *    Fernando's Firebase UID maps to `fernando-hidalgo`; we always use the
 *    canonical account VM doc, never the deprecated UID-keyed VM doc.
 *
 * No auth tokens and no identity are placed in the iframe URL (since
 * 2026-09-27 not even the account or the person: the page takes the signed-in
 * uid from the shell's verified voiceAuth message, never from its address).
 */
import { useCallback, useEffect, useState } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getApp } from 'firebase/app';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { resolveAccount } from '../services/prest0nRuntime/accountResolver';
import { ACCOUNT_FIRESTORE_PREFIX } from '../services/prest0nRuntime/constants';
import type { AccountVmConfig } from '../services/prest0nRuntime/types';
import { buildTargetUrl, isSafeAccountVmBase, keptFrameAddress } from './realtimeDesktopUrl';
import { readBrowserServiceKeyFresh } from '../services/browserService/baseUrl';
// Internal allowlist for full-Preston access — shared module since task #56
// (the onboarding workspace gate consults the same list; keep it in one place).
import { isInternalAccount, PREST0_INC_ACCOUNT_ID } from '../config/internalAccess';
import { INTERNSHIP_WORDS } from '../config/internshipWords';

// 2026-05-13: Internal Preston VM is prest0-vm (34.60.222.164), NOT the
// Fernando tenant VM. The 2026-05-12 patch that flipped this to
// 34-121-30-211 was incorrect — that hostname resolves to the Fernando
// tenant, which is a customer tenant and not the right target for internal
// Prest0 users (Justin / Miles / prest0n@). Flipping back to prest0-vm,
// where the real internal Preston (avatar + browser-service + Simli
// pipeline) runs.
const DEFAULT_INTERNAL_BASE = 'https://34-60-222-164.sslip.io';

function resolveInternalBase(): string {
  try {
    // Optional runtime override (e.g. for staging).
    const override = (window as unknown as { PREST0_REALTIME_INTERNAL_BASE?: string })
      .PREST0_REALTIME_INTERNAL_BASE;
    if (override && typeof override === 'string' && override.trim()) {
      return override.trim();
    }
  } catch {
    // SSR / non-browser contexts — ignore.
  }
  return DEFAULT_INTERNAL_BASE;
}

function isInternalUser(opts: { uid: string; email: string | null }): boolean {
  return isInternalAccount(opts);
}

// ─────────────────────────────────────────────────────────────────────────────
// Public hook
// ─────────────────────────────────────────────────────────────────────────────

export interface RealtimeDesktopTarget {
  loading: boolean;
  accountId: string | null;
  baseUrl: string | null;
  targetUrl: string | null;
  source: 'internal' | 'account-vm' | 'none';
  error: string | null;
  /**
   * Instant-workspace boot state (2026-08-02): true while the account's VM
   * doc reports status 'provisioning' — the workstation is being built and
   * the desktop surface must render the boot card instead of an iframe.
   */
  provisioning: boolean;
  provisioningStartedAtMs: number | null;
  /**
   * Idle-lifecycle state (2026-08-18): 'asleep' (suspended/stopped — render
   * the asleep card with the wake CTA, never the iframe: the host is down
   * and its baseUrl is stale), 'waking' (resume in flight — render the
   * waking card and poll boot status), 'archived' (instance deleted;
   * rebuild path). 'active' for live and legacy docs. 'rebuilding'
   * (2026-09-05, workstation plan PR-10): a wake that is REBUILDING the
   * workstation from its saved snapshot — vm/main.rebuildRequestedAt stamped
   * within the current wake (wakeRequestedAt); several minutes, not 45–90 s.
   * Every 'waking' consumer treats it as waking; only the copy differs.
   */
  lifecycle: 'active' | 'asleep' | 'waking' | 'rebuilding' | 'archived';
  /** Why it's asleep — 'billing' renders payment copy instead of a wake CTA. */
  stopReason: string | null;
  /**
   * Per-VM browser-service bearer (2026-08-20): /browser-api/api/* on the VM
   * is token-gated (2026-08-19 P0 hardening); the embedded avatar page cannot
   * read Firestore itself, so the shell reads vm/main.browserServiceToken
   * (org-member-gated) and hands it to the iframe over postMessage — never
   * via the iframe URL. Optional: only the account-vm path carries it.
   */
  browserServiceToken?: string | null;
  /**
   * Last visit's target, painted before this visit's resolve has landed (the warm start below). The resolve
   * replaces it, and drops this mark. A reader that judges which machine the page is on waits for the resolve
   * (2026-10-06): a warm target can be a machine that has since moved.
   */
  warm?: true;
}

export interface RealtimeDesktopTargetWithRefresh extends RealtimeDesktopTarget {
  /** Re-run target resolution (e.g. after the boot poller reports 'ready'). */
  refresh: () => void;
}


/**
 * WARM START (2026-09-02, owner: "the desktop page is not loading fast enough —
 * it needs to be warm and loaded immediately").
 *
 * Resolving the target is two SERIAL Firestore round trips after auth
 * (account_membership → vm/main), and every branch in AgentActivity gates on
 * `loading`. So a returning user watched a spinner for both trips before the
 * iframe was even given a URL — with the VM already awake and the page already
 * cached. The waiting was ours, not the VM's.
 *
 * The endpoint for an account is stable between visits, so the last known one
 * is a good first guess. We seed state from it, render the frame immediately,
 * and let the real resolve land underneath and correct anything that moved.
 *
 * Three deliberate limits, because a wrong warm start is worse than a slow one:
 *  • keyed by uid, and a cache written by another uid is never read — a VM
 *    endpoint must never leak across accounts;
 *  • only an ACTIVE, non-provisioning target is ever cached or replayed.
 *    Lifecycle is exactly the thing that goes stale (asleep, waking, archived,
 *    billing), and replaying it would show a confident wrong card. Those states
 *    keep today's honest loading path;
 *  • bounded by age. sslip.io hostnames encode the VM's IP, and a
 *    suspend/resume changes it, so a long-stale URL points at nothing.
 */
const WARM_KEY = 'prest0_desktop_target_warm';
const WARM_MAX_AGE_MS = 24 * 60 * 60 * 1000;

type WarmEntry = {
  uid: string;
  accountId: string | null;
  baseUrl: string | null;
  targetUrl: string | null;
  source: RealtimeDesktopTarget['source'];
  savedAtMs: number;
};

/**
 * Pure: the state a warm cache entry should seed, or null for "resolve
 * normally". Exported because the hook itself cannot be rendered in this
 * repo's test setup (no renderHook), and a warm start that is only checked by
 * grepping source text is not checked at all — the first version of this
 * feature shipped two defects past exactly such tests.
 */
export function warmSeedState(
  uid: string | null,
  storage: Pick<Storage, 'getItem'> | null = typeof window === 'undefined' ? null : window.localStorage,
): RealtimeDesktopTarget | null {
  const warm = readWarmTarget(uid, storage);
  if (!warm) return null;
  return {
    ...INITIAL,
    loading: false,
    accountId: warm.accountId,
    baseUrl: warm.baseUrl,
    targetUrl: warm.targetUrl,
    source: warm.source,
    warm: true,
  };
}

function readWarmTarget(
  uid: string | null,
  storage: Pick<Storage, 'getItem'> | null = typeof window === 'undefined' ? null : window.localStorage,
): WarmEntry | null {
  if (!uid || !storage) return null;
  try {
    const raw = storage.getItem(WARM_KEY);
    if (!raw) return null;
    const e = JSON.parse(raw) as WarmEntry;
    if (!e || e.uid !== uid || !e.targetUrl || !e.baseUrl) return null;
    if (!Number.isFinite(e.savedAtMs) || Date.now() - e.savedAtMs > WARM_MAX_AGE_MS) return null;
    return e;
  } catch {
    return null; // private mode, cleared storage, malformed — just resolve normally
  }
}

function writeWarmTarget(uid: string | null, t: RealtimeDesktopTarget): void {
  if (!uid) return;
  try {
    // Only a settled, usable target is worth replaying.
    if (t.loading || t.error || t.provisioning || t.lifecycle !== 'active' || !t.targetUrl) {
      window.localStorage.removeItem(WARM_KEY);
      return;
    }
    const e: WarmEntry = {
      uid,
      accountId: t.accountId,
      baseUrl: t.baseUrl,
      targetUrl: t.targetUrl,
      source: t.source,
      savedAtMs: Date.now(),
    };
    window.localStorage.setItem(WARM_KEY, JSON.stringify(e));
  } catch {
    /* storage unavailable — warm start is an optimisation, never a requirement */
  }
}

const INITIAL: RealtimeDesktopTarget = {
  loading: true,
  accountId: null,
  baseUrl: null,
  targetUrl: null,
  source: 'none',
  error: null,
  provisioning: false,
  provisioningStartedAtMs: null,
  lifecycle: 'active',
  stopReason: null,
};

/** How long the desktop waits to learn whether a personal workspace has a plan before it keeps its usual words. */
const PERSONAL_PLAN_READ_MS = 4000;

/** Whether the person's own subscription document exists (users/{uid}/subscription/subscription). */
async function ownSubscriptionExists(uid: string): Promise<boolean> {
  const snap = await getDoc(doc(getFirestore(getApp()), 'users', uid, 'subscription', 'subscription'));
  return snap.exists();
}

/**
 * Whether this person's own workspace has no plan at all (W-R2a, Justin 2026-10-30): activatePersonalWorkspace starts
 * none, so it has no subscription document until its person hires or redeems a code. A read that fails or takes too
 * long answers false: the desktop then keeps its usual words. The reader is injectable for tests.
 */
export async function personalWorkspaceHasNoPlan(
  uid: string,
  subscriptionExists: (uid: string) => Promise<boolean> = ownSubscriptionExists,
  waitMs: number = PERSONAL_PLAN_READ_MS,
): Promise<boolean> {
  const read = subscriptionExists(uid).then((exists) => !exists, () => false);
  let timer: ReturnType<typeof setTimeout> | null = null;
  const late = new Promise<boolean>((resolve) => { timer = setTimeout(() => resolve(false), waitMs); });
  try {
    return await Promise.race([read, late]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * What /desktop says for a workspace outside an organization with no workstation record: a person's own plan-less
 * personal workspace runs no workstation (provisionUserVM refuses it until its person hires, redeems a code or creates
 * an organization), so it hears where the free internship is; anything else is still being set up.
 */
export function noWorkstationWords(personalNoPlan: boolean): string {
  return personalNoPlan
    ? INTERNSHIP_WORDS.personalStop
    : "your workspace's desktop is being set up — it starts automatically. tap retry in a moment; chat already works. if this keeps showing after a few minutes, contact support.";
}

/**
 * Async core: given a signed-in Firebase user, decide which VM endpoint to
 * use and produce a fully composed iframe URL.
 */
async function resolveTargetForUser(firebaseUser: {
  uid: string;
  email: string | null;
}): Promise<RealtimeDesktopTarget> {
  const uid = firebaseUser.uid;
  const email = firebaseUser.email ?? null;
  const internal = isInternalUser({ uid, email });

  // ── Branch 1: internal Prest0 user IN THE PREST0 INC WORKSPACE ──────────
  // (2026-08-21 owner report: switching between 4 orgs showed the same
  // "Internal Preston VM" tag on every one.) The internal short-circuit used
  // to key on WHO is signed in, never on WHICH org is active — a single-org
  // assumption from before multi-org. Doctrine: every organization owns its
  // own VM and accountId is the join key, so the internal VM (prest0-vm, the
  // Slack lane) is the target only when the ACTIVE workspace resolves to
  // Prest0 Inc — or to nothing at all (internal accounts often carry no
  // profile doc; the internal base is their only home). Every other org
  // falls through to its own accounts/{id}/vm/main.
  const internalResolution = internal ? await resolveAccount(uid).catch(() => null) : null;
  const internalResolvedId = internalResolution?.accountId ?? null;
  if (internal && (!internalResolvedId || internalResolvedId === PREST0_INC_ACCOUNT_ID)) {
    const baseUrl = resolveInternalBase();
    // For internal users we don't have (or need) a canonical accountId; pass
    // 'prest0-internal' as a stable label so the embedded app can log it.
    const accountId = 'prest0-internal';
    // The internal machine's key (2026-09-24). This branch carried none: harmless while prest0-vm
    // had no key, and a 401 on every desktop call from the page the moment it got one (04:24 UTC
    // that day), because the page asks the shell for the key and the shell had nothing to give.
    // It is the shared internal key, the one the chat's sign-in window sends to the same host,
    // read past the page cache so a key installed after this page loaded is the one handed on.
    const browserServiceToken = await readBrowserServiceKeyFresh({ kind: 'internal' });
    return {
      loading: false,
      accountId,
      baseUrl,
      targetUrl: buildTargetUrl(baseUrl),
      source: 'internal',
      error: null,
      provisioning: false,
      provisioningStartedAtMs: null,
      lifecycle: 'active',
      stopReason: null,
      browserServiceToken,
    };
  }

  // ── Branch 2: account-scoped tenant routing ──────────────────────────────
  const resolution = internal ? internalResolution : await resolveAccount(uid);

  // Fallback (2026-07-31): some provisioned accounts have a live uid-keyed
  // VM doc but never got accounts/{uid}/profile/main written (the test-mode
  // provisioning path wrote it only in an unawaited post-create callback).
  // resolveAccount() returns null for them even though their VM is ready —
  // resolve against the VM doc directly instead of dead-ending the desktop.
  // Guard: membership-mapped users (canonical accountId ≠ authUid, e.g.
  // Fernando → fernando-hidalgo) must NEVER fall back to the deprecated
  // uid-keyed VM doc — a transient resolveAccount error on such an account
  // keeps the safe error path instead.
  let accountId: string | null = resolution?.accountId ?? null;
  if (!accountId) {
    try {
      const db = getFirestore(getApp());
      const membershipSnap = await getDoc(doc(db, 'users', uid, 'account_membership', 'main'));
      if (!membershipSnap.exists()) {
        const fallbackVmSnap = await getDoc(doc(db, ACCOUNT_FIRESTORE_PREFIX, uid, 'vm', 'main'));
        if (fallbackVmSnap.exists()) {
          accountId = uid;
        }
      }
    } catch {
      /* fall through to the no-account error below */
    }
  }

  if (!accountId) {
    // SELF-HEAL, never a dead end (2026-09-09). This was the only one of six outcomes here that
    // told the user to go find an admin — about their OWN personal workspace, where there is no
    // admin to find. It fired when a user switched into a workspace whose accounts/{uid}/profile/
    // main had never been minted: switchActiveWorkspace writes the back-pointer but no structure,
    // and the uid-keyed VM fallback above is skipped precisely BECAUSE that switch created the
    // membership doc it tests for.
    //
    // ensureNativeOrg is the callable that mints the missing structure, and it is idempotent —
    // WorkspacePill already calls it on every authenticated mount. Firing it here makes retry mean
    // something, exactly as the two branches below already do for the org planes.
    void (async () => {
      try {
        const { getFunctions, httpsCallable } = await import('firebase/functions');
        await httpsCallable(getFunctions(undefined, 'us-central1'), 'ensureNativeOrg')({});
      } catch { /* best-effort — retry re-resolves */ }
    })();
    return {
      loading: false,
      accountId: null,
      baseUrl: null,
      targetUrl: null,
      source: 'none',
      error:
        "your workspace is still being set up — it's finishing automatically. tap try again in a moment; your chat and everything you've set up are ready now.",
      provisioning: false,
      provisioningStartedAtMs: null,
      lifecycle: 'active',
      stopReason: null,
    };
  }

  // Direct Firestore read so we get the new optional fields (baseUrl etc.)
  // even if older callers haven't been updated.
  let vmConfig: Partial<AccountVmConfig> | null = null;
  try {
    const db = getFirestore(getApp());
    const vmRef = doc(db, ACCOUNT_FIRESTORE_PREFIX, accountId, 'vm', 'main');
    const snap = await getDoc(vmRef);
    if (snap.exists()) {
      vmConfig = snap.data() as Partial<AccountVmConfig>;
    }
  } catch (err) {
    console.warn('[useRealtimeDesktopTarget] Failed to load VM doc', err);
  }

  const baseUrl =
    typeof vmConfig?.baseUrl === 'string' && vmConfig.baseUrl.trim()
      ? vmConfig.baseUrl.trim()
      : null;

  // Idle-lifecycle states (2026-08-18) are checked BEFORE the legacy status
  // branches: an asleep workstation still carries status 'active' and its
  // stale (dead) baseUrl, so falling through would render exactly the dead
  // iframe the lifecycle field exists to prevent.
  const lifecycleState = (vmConfig as any)?.lifecycleState;
  if (lifecycleState === 'suspended' || lifecycleState === 'stopped'
    || lifecycleState === 'waking' || lifecycleState === 'deleting' || lifecycleState === 'deleted') {
    const wakeRaw = (vmConfig as any)?.wakeRequestedAt;
    const wakeMs =
      typeof wakeRaw?.toMillis === 'function' ? wakeRaw.toMillis()
      : typeof wakeRaw?.seconds === 'number' ? wakeRaw.seconds * 1000
      : null;
    const rebuildRaw = (vmConfig as any)?.rebuildRequestedAt;
    const rebuildMs =
      typeof rebuildRaw?.toMillis === 'function' ? rebuildRaw.toMillis()
      : typeof rebuildRaw?.seconds === 'number' ? rebuildRaw.seconds * 1000
      : null;
    // A rebuild stamp counts only when it belongs to THIS wake (a stale one
    // from an earlier rebuild must not relabel a later ordinary wake).
    const rebuilding = lifecycleState === 'waking' && rebuildMs !== null
      && (wakeMs === null || rebuildMs >= wakeMs - 60_000);
    const lifecycle =
      lifecycleState === 'waking' ? (rebuilding ? 'rebuilding' as const : 'waking' as const)
      : lifecycleState === 'suspended' || lifecycleState === 'stopped' ? 'asleep' as const
      : 'archived' as const;
    return {
      loading: false,
      accountId,
      baseUrl,
      targetUrl: null,
      source: 'account-vm',
      error: null,
      provisioning: false,
      provisioningStartedAtMs: wakeMs,
      lifecycle,
      stopReason: typeof (vmConfig as any)?.stopReason === 'string' ? (vmConfig as any).stopReason : null,
    };
  }

  // Instant-workspace boot state (2026-08-02): a vm/main doc with status
  // 'provisioning' means the workstation is being built right now. Never
  // iframe it (the bootstrap takes minutes after the URL exists — that's
  // exactly the dead "refused to connect" frame this replaces); render the
  // boot card and let the boot poller drive the flip to ready.
  if ((vmConfig as any)?.status === 'provisioning') {
    const startedRaw = (vmConfig as any)?.provisioningStartedAt;
    const startedMs =
      typeof startedRaw?.toMillis === 'function' ? startedRaw.toMillis()
      : typeof startedRaw?.seconds === 'number' ? startedRaw.seconds * 1000
      : null;
    return {
      loading: false,
      accountId,
      baseUrl,
      targetUrl: null,
      source: 'account-vm',
      error: null,
      provisioning: true,
      provisioningStartedAtMs: startedMs,
      lifecycle: 'active',
      stopReason: null,
    };
  }

  if (baseUrl && isSafeAccountVmBase(baseUrl)) {
    return {
      loading: false,
      accountId,
      baseUrl,
      targetUrl: buildTargetUrl(baseUrl),
      source: 'account-vm',
      error: null,
      provisioning: false,
      provisioningStartedAtMs: null,
      lifecycle: 'active',
      stopReason: null,
      browserServiceToken:
        typeof (vmConfig as { browserServiceToken?: unknown } | null)?.browserServiceToken === 'string' &&
        (vmConfig as { browserServiceToken?: string }).browserServiceToken!.trim()
          ? (vmConfig as { browserServiceToken?: string }).browserServiceToken!.trim()
          : null,
    };
  }

  if (baseUrl) {
    // Present but not an https origin — corrupt doc; never iframe it.
    console.warn('[useRealtimeDesktopTarget] refusing non-https account VM base URL');
    return {
      loading: false,
      accountId,
      baseUrl: null,
      targetUrl: null,
      source: 'none',
      error: "Your Prest0n desktop isn't configured correctly. Please contact support.",
      provisioning: false,
      provisioningStartedAtMs: null,
      lifecycle: 'active',
      stopReason: null,
    };
  }

  // No VM base URL assigned to this account — non-internal users cannot fall
  // back to the internal Preston brain. In the eager-VM era (2026-08-19,
  // owner decision: EVERY created org gets its VM at onboarding) a co_ org
  // with no vm/main doc means the eager kick failed or predates the change —
  // fire a best-effort self-heal provision (server enforces ownership; a
  // member's call just 403s) and report the honest "being prepared" state so
  // /desktop's retry lands on the provisioning card, never a support
  // dead-end (task #75 review finding 21; support cannot "fix" it).
  if (accountId.startsWith('co_')) {
    void (async () => {
      try {
        const fbUser = getAuth().currentUser;
        if (!fbUser) return;
        const token = await fbUser.getIdToken();
        const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'tensile-tenure-453409-k3';
        await fetch(`https://us-central1-${projectId}.cloudfunctions.net/provisionUserVM`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ accountId }),
        });
      } catch { /* best-effort — retry re-resolves */ }
    })();
    return {
      loading: false,
      accountId,
      baseUrl: null,
      targetUrl: null,
      source: 'none',
      error: "this organization's workstation is being prepared — it starts building automatically. tap retry in a moment; chat and everything you set up are ready now.",
      provisioning: false,
      provisioningStartedAtMs: null,
      lifecycle: 'active',
      stopReason: null,
    };
  }
  // Non-co_ account with no VM (INCIDENT-20260824): post-D1 the uid plane is
  // serverless BY DESIGN — a trial-code account landing here means its
  // organization was never created (the pre-fix redemption lane) or the
  // redemption-time attempt failed. Fire the idempotent server-side repair:
  // ensureTrialOrgWorkspace creates the co_ org with the code's terms and
  // kicks its machine; the next retry resolves the org and takes the co_
  // self-heal path above. Accounts with no redeemed trial are refused
  // server-side and simply keep this card — with honest guidance instead of
  // an immediate "contact support" dead-end.
  void (async () => {
    try {
      const fbUser = getAuth().currentUser;
      if (!fbUser) return;
      const token = await fbUser.getIdToken();
      const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'tensile-tenure-453409-k3';
      // onCall protocol: POST {data:{}}. When the repair reports progress
      // (org created or machine kicked), reload ONCE so resolution re-runs
      // against the repaired state instead of waiting for a manual retry —
      // throttled via sessionStorage so a failing repair can never reload-loop.
      const resp = await fetch(`https://us-central1-${projectId}.cloudfunctions.net/ensureTrialOrgWorkspace`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: {} }),
      });
      const body = await resp.json().catch(() => null);
      const r = body?.result ?? body;
      if (resp.ok && r && (r.created === true || r.vmKicked === true)) {
        let last = 0;
        try { last = Number(sessionStorage.getItem('prest0nTrialOrgRepairReloadAt')) || 0; } catch { /* storage may throw */ }
        if (Date.now() - last > 60_000) {
          try { sessionStorage.setItem('prest0nTrialOrgRepairReloadAt', String(Date.now())); } catch { /* best-effort */ }
          setTimeout(() => window.location.reload(), 1500);
        }
      }
    } catch { /* best-effort — retry re-resolves */ }
  })();
  // A person's own personal workspace with no plan (W-R2a, Justin 2026-10-30) runs no workstation: provisionUserVM
  // refuses it until its person hires, redeems a code or creates an organization. The desktop says where the free
  // internship is (INTERNSHIP_WORDS.personalStop), never that a desktop is on its way.
  const personalNoPlan = accountId === uid && await personalWorkspaceHasNoPlan(uid);
  return {
    loading: false,
    accountId,
    baseUrl: null,
    targetUrl: null,
    source: 'none',
    error: noWorkstationWords(personalNoPlan),
    provisioning: false,
    provisioningStartedAtMs: null,
    lifecycle: 'active',
    stopReason: null,
  };
}

/**
 * React hook that produces the realtime desktop iframe target for the
 * currently signed-in user. Recomputes on auth state changes.
 */
export function useRealtimeDesktopTarget(): RealtimeDesktopTargetWithRefresh {
  // Seeded lazily so the very first render already carries last visit's
  // endpoint: the iframe starts loading on first paint instead of after two
  // serial Firestore round trips. The real resolve runs immediately below and
  // overwrites this, so a moved VM self-corrects within the same visit.
  // useAuth FIRST: the seed below needs a uid, and Firebase's currentUser is
  // restored asynchronously from IndexedDB — on the hard refresh this feature
  // exists for it is still null at first render. The app's own AuthContext is
  // available synchronously, and recompute() already treats it as the
  // authoritative fallback (see effectiveUid below); the seed must use the same
  // rule or it silently never fires in the one case that matters.
  const { isAuthenticated, user } = useAuth();
  const seedUid = (() => {
    try {
      const fb = getAuth(getApp()).currentUser?.uid ?? null;
      return fb || (isAuthenticated ? (user?.id ?? null) : null);
    } catch {
      return isAuthenticated ? (user?.id ?? null) : null;
    }
  })();
  const [state, setState] = useState<RealtimeDesktopTarget>(() => warmSeedState(seedUid) ?? INITIAL);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const auth = getAuth(getApp());

    async function recompute(currentUid: string | null, currentEmail: string | null) {
      // ProtectedRoute permits either Firebase Auth OR the app AuthContext.
      // On some restored sessions Firebase currentUser is still null while
      // local app auth is already valid; if we only trust Firebase here, the
      // /desktop shell renders with no realtime widget. Fall back to AuthContext
      // so the resolver matches the route guard.
      const effectiveUid = currentUid || (isAuthenticated ? (user?.id ?? null) : null);
      const effectiveEmail = currentEmail || (isAuthenticated ? (user?.email ?? null) : null);

      if (!effectiveUid) {
        if (cancelled) return;
        setState({
          loading: false,
          accountId: null,
          baseUrl: null,
          targetUrl: null,
          source: 'none',
          error: 'Please sign in to access your Prest0n desktop.',
          provisioning: false,
          provisioningStartedAtMs: null,
          lifecycle: 'active',
          stopReason: null,
        });
        return;
      }

      if (cancelled) return;
      // Keep a warm-painted target MOUNTED while the real resolve runs. Flipping
      // loading back to true here unmounts the iframe (AgentActivity gates every
      // branch on !loading), so the user saw the desktop appear and then be
      // replaced by a spinner — worse than not warming at all. With nothing warm
      // to show, this is the original honest loading path.
      setState((prev) =>
        prev.targetUrl && !prev.loading
          ? { ...prev, error: null }
          : { ...prev, loading: true, error: null },
      );

      try {
        const next = await resolveTargetForUser({
          uid: effectiveUid,
          email: effectiveEmail,
        });
        if (cancelled) return;
        // A re-resolve that finds the same workstation keeps the frame's address, so the live page —
        // and the voice conversation running in it — is not reloaded (keptFrameAddress). A warm seed
        // is last visit's address: the first real resolve still replaces it, as it always did.
        setState((prev) => (prev.warm ? next : { ...next, targetUrl: keptFrameAddress(prev.targetUrl, next.targetUrl) }));
        // Record (or clear) the warm start for the next visit. Clearing on a
        // non-active result is the important half: it is what stops a warm
        // start replaying an endpoint after the VM went to sleep.
        writeWarmTarget(effectiveUid, next);
      } catch (err) {
        if (cancelled) return;
        console.error('[useRealtimeDesktopTarget] resolve failed', err);
        setState({
          loading: false,
          accountId: null,
          baseUrl: null,
          targetUrl: null,
          source: 'none',
          error:
            'Failed to load your Prest0n desktop. Please refresh the page.',
          provisioning: false,
          provisioningStartedAtMs: null,
          lifecycle: 'active',
          stopReason: null,
        });
      }
    }

    // Initial sync attempt against whatever Firebase already has; recompute()
    // falls back to app AuthContext when Firebase has not restored yet.
    void recompute(
      auth.currentUser?.uid ?? null,
      auth.currentUser?.email ?? null,
    );

    // Track auth changes so the iframe updates if the user signs in/out.
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user || user.isAnonymous) {
        void recompute(null, null);
        return;
      }
      void recompute(user.uid, user.email ?? null);
    });

    return () => {
      cancelled = true;
      try {
        unsubscribe();
      } catch {
        /* noop */
      }
    };
  }, [isAuthenticated, user?.id, user?.email, refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  return { ...state, refresh };
}

export default useRealtimeDesktopTarget;
